import { useMemo, useCallback } from 'react';
import {
  inferType,
  traverseProductObject,
  formatFieldNameForError,
  type SourceFieldInfo
} from './services';

export interface DestinationFieldInfo {
  fieldId: string;
  fieldDataTypeId: string;
  displayName: string;
}

export interface ValidationResult {
  isValid: boolean;
  errorMessage: string | null;
}

/**
 * Analyzes API response and builds a complete map of source fields with inferred types
 * Traverses both product data and price array to catalog all available fields.
 * Prices are keyed `price.prices.<PRICE_TYPE_IDENTIFIER>`, the path the mapping
 * rules and every session override address them by, so a lookup here and a
 * mapping row cannot disagree.
 * @param productPriceAndAvailabilityData - Combined product and price data from Signify API
 * @returns Map of field paths to their inferred type information
 */
const inferSourceTypes = (
  productPriceAndAvailabilityData: any,
  extraSourceData?: Array<{ data: Record<string, unknown>; prefix: string }>
): Map<string, SourceFieldInfo> => {
  const map = new Map<string, SourceFieldInfo>();

  if (!productPriceAndAvailabilityData) {
    return map;
  }

  if (productPriceAndAvailabilityData.product) {
    traverseProductObject(productPriceAndAvailabilityData.product, 'product', map);
  }

  if (productPriceAndAvailabilityData.price?.prices && Array.isArray(productPriceAndAvailabilityData.price.prices)) {
    productPriceAndAvailabilityData.price.prices.forEach((price: any) => {
      if (price.priceTypeIdentifier) {
        const displayValue = price.formattedValue !== undefined ? price.formattedValue : price.value;
        const inferredType = inferType(price.value, displayValue);
        const path = `price.prices.${price.priceTypeIdentifier}`;

        map.set(path, {
          path,
          rawValue: price,
          displayValue,
          inferredType
        });
      }
    });
  }

  if (productPriceAndAvailabilityData.availability) {
    traverseProductObject(productPriceAndAvailabilityData.availability, 'availability', map);
  }

  if (productPriceAndAvailabilityData.defaultPlannedDeliveryTime != null) {
    const value = productPriceAndAvailabilityData.defaultPlannedDeliveryTime;
    map.set('defaultPlannedDeliveryTime', {
      path: 'defaultPlannedDeliveryTime',
      rawValue: value,
      displayValue: value,
      inferredType: inferType(value, value)
    });
  }

  if (extraSourceData && extraSourceData.length > 0) {
    extraSourceData.forEach((source) => {
      if (source.data) {
        traverseProductObject(source.data, source.prefix, map);
      }
    });
  }

  return map;
};

interface ValidateMappingProps {
  sourceField: SourceFieldInfo;
  destinationField: DestinationFieldInfo;
}

/**
 * Validates a single mapping between source and destination fields
 * Checks type compatibility and returns validation result with error message if incompatible
 * @param props - Source and destination field information
 * @returns Validation result indicating if the mapping is valid
 */
const validateMapping = ({
  sourceField,
  destinationField
}: ValidateMappingProps): ValidationResult => {
  const destType = destinationField.fieldDataTypeId;
  const sourceType = sourceField.inferredType;

  if (['text', 'shortText', 'longText'].includes(destType)) {
    return { isValid: true, errorMessage: null };
  }

  const sourceName = formatFieldNameForError(sourceField.path);
  const destName = destinationField.displayName;

  if (sourceType === 'object') {
    return {
      isValid: false,
      errorMessage: `${sourceName} (detected as object) does not match the format of ${destName} (${destType})`
    };
  }

  if (sourceType === 'currency') {
    if (destType === 'currency') {
      return { isValid: true, errorMessage: null };
    }
    return {
      isValid: false,
      errorMessage: `${sourceName} (detected as currency) does not match the format of ${destName} (${destType})`
    };
  }

  if (sourceType === 'percentage') {
    if (destType === 'percentage') {
      return { isValid: true, errorMessage: null };
    }
    return {
      isValid: false,
      errorMessage: `${sourceName} (detected as percentage) does not match the format of ${destName} (${destType})`
    };
  }

  if (sourceType === 'number') {
    if (['currency', 'percentage', 'number'].includes(destType)) {
      return { isValid: true, errorMessage: null };
    }
    return {
      isValid: false,
      errorMessage: `${sourceName} (detected as number) does not match the format of ${destName} (${destType})`
    };
  }

  if (sourceType === 'date') {
    if (['date', 'dateTime'].includes(destType)) {
      return { isValid: true, errorMessage: null };
    }
    return {
      isValid: false,
      errorMessage: `${sourceName} (detected as date) does not match the format of ${destName} (${destType})`
    };
  }

  if (sourceType === 'boolean') {
    if (destType === 'yesNo') {
      return { isValid: true, errorMessage: null };
    }
    return {
      isValid: false,
      errorMessage: `${sourceName} (detected as boolean) does not match the format of ${destName} (${destType})`
    };
  }

  // Text type can only map to text-like fields
  if (sourceType === 'text') {
    if (['text', 'shortText', 'longText'].includes(destType)) {
      return { isValid: true, errorMessage: null };
    }
    return {
      isValid: false,
      errorMessage: `${sourceName} (detected as text) does not match the format of ${destName} (${destType})`
    };
  }

  // For any other unhandled source types, only allow text destinations
  return { isValid: true, errorMessage: null };
};

interface UseSignifyMappingValidationProps {
  productPriceAndAvailabilityData: any;
  extraSourceData?: Array<{ data: Record<string, unknown>; prefix: string }>;
  availableLineItemFields: Array<{ 
    fieldId: string; 
    label: string; 
    fieldDataTypeId: string;
  }>;
}

/**
 * Hook for validating Signify field mappings with simplified logic
 * - Infers types from source API data once
 * - Maps destination field types once with memoization
 * - Validates individual mappings on-demand
 */
export const useSignifyMappingValidation = ({
  productPriceAndAvailabilityData,
  extraSourceData,
  availableLineItemFields
}: UseSignifyMappingValidationProps) => {
  const sourceTypesMap = useMemo(() => {
    return inferSourceTypes(productPriceAndAvailabilityData, extraSourceData);
  }, [extraSourceData, productPriceAndAvailabilityData]);

  const destinationFieldsMap = useMemo(() => {
    const map = new Map<string, DestinationFieldInfo>();
    
    availableLineItemFields.forEach(field => {
      map.set(field.fieldId, {
        fieldId: field.fieldId,
        fieldDataTypeId: field.fieldDataTypeId,
        displayName: field.label
      });
    });

    return map;
  }, [availableLineItemFields]);

  const validateSingleMapping = useCallback((apiField: string, lineItemField: string): ValidationResult => {
    const source = sourceTypesMap.get(apiField);
    const dest = destinationFieldsMap.get(lineItemField);

    if (!source) {
      return {
        isValid: false,
        errorMessage: `Source field "${apiField}" not found in API data`
      };
    }

    if (!dest) {
      return {
        isValid: false,
        errorMessage: `Destination field "${lineItemField}" not found`
      };
    }

    return validateMapping({ sourceField: source, destinationField: dest });
  }, [sourceTypesMap, destinationFieldsMap]);

  return {
    sourceTypesMap,
    destinationFieldsMap,
    validateSingleMapping
  };
};

